import React from "react";
import { Link } from "react-router-dom";
import { STOREFRONT_DASHBOARD } from "../../../data/storefrontDashboardSeed";

export default function StorefrontDashboardView({
  heroArt,
  parcelArt,
  characterArt,
  storeCount,
  err,
  blocked,
  blockedMessage,
}) {
  const d = STOREFRONT_DASHBOARD;
  const { hero, activeUtility, modules, rightRail } = d;

  return (
    <div className="sf-dash">
      {err || blocked ? (
        <div className="sf-dash__alerts">
          {err ? (
            <div className="mp-banner mp-banner--warn" role="status">
              {err} — preview layout continues below for visual QA.
            </div>
          ) : null}
          {blocked ? (
            <div className="mp-banner mp-banner--warn">
              <strong>Ownership verification unavailable on this server.</strong>{" "}
              {blockedMessage || "Configure Oshar real estate verification to unlock eligibility checks."}
            </div>
          ) : null}
        </div>
      ) : null}
      {storeCount != null && storeCount > 0 ? (
        <p className="sf-dash__meta">
          Read-only: this account has <strong>{storeCount}</strong> linked storefront record
          {storeCount !== 1 ? "s" : ""} in the database. Management UIs remain disabled in this phase.
        </p>
      ) : null}

      <div className="sf-dash__grid">
        <div className="sf-dash__main">
          <section
            className="sf-hero"
            style={{ "--sf-hero-bg": `url(${heroArt})` }}
            aria-labelledby="sf-hero-title"
          >
            <div className="sf-hero__overlay" aria-hidden />
            <div className="sf-hero__inner">
              <p className="sf-hero__eyebrow">{hero.eyebrow}</p>
              <h1 id="sf-hero-title" className="sf-hero__title serif">
                {hero.title}
              </h1>
              <p className="sf-hero__subtitle serif">{hero.subtitle}</p>
              <p className="sf-hero__desc">{hero.description}</p>
              <div className="sf-hero__locked">
                <p className="sf-hero__locked-title">{hero.statusTitle}</p>
                <p className="sf-hero__locked-body">{hero.statusBody}</p>
              </div>
              <div className="sf-hero__ribbon">
                <strong>Preview only</strong>
                <span>Panels are non-functional — no writes, uploads, or payouts.</span>
              </div>
            </div>
          </section>

          <section className="sf-utility" aria-labelledby="sf-utility-heading">
            <h2 id="sf-utility-heading" className="sf-utility__head">
              {activeUtility.sectionTitle}
            </h2>
            <div className="sf-utility__card">
              <div className="sf-utility__thumb">
                <img src={parcelArt} alt="" loading="lazy" decoding="async" />
              </div>
              <div>
                <div className="sf-utility__badges">
                  <span className="sf-chip sf-chip--nft">NFT Real Estate Utility</span>
                  <span className="sf-chip">{activeUtility.badge}</span>
                </div>
                <h3 className="sf-utility__title serif">{activeUtility.parcel}</h3>
                <dl className="sf-utility__dl">
                  <div>
                    <dt>District</dt>
                    <dd>{activeUtility.district}</dd>
                  </div>
                  <div>
                    <dt>Property class</dt>
                    <dd>{activeUtility.propertyClass}</dd>
                  </div>
                  <div>
                    <dt>Commerce type</dt>
                    <dd>{activeUtility.commerceType}</dd>
                  </div>
                  <div>
                    <dt>Utility</dt>
                    <dd>{activeUtility.utility}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{activeUtility.status}</dd>
                  </div>
                </dl>
                <p className="sf-utility__note">{activeUtility.description}</p>
              </div>
            </div>
          </section>

          <section className="sf-modules" aria-labelledby="sf-modules-heading">
            <h2 id="sf-modules-heading" className="sf-modules__head">
              Merchant modules
            </h2>
            <p className="sf-modules__lede">Non-interactive — commerce core offline. Icon frames are decorative only.</p>
            <div className="sf-modules__grid">
              {modules.map((m) => (
                <article key={m.title} className="sf-mod" aria-disabled="true">
                  <div className="sf-mod__icon" aria-hidden>
                    <i className={`fas ${m.iconClass}`} />
                  </div>
                  <h3 className="sf-mod__title serif">{m.title}</h3>
                  <p className="sf-mod__desc">{m.description}</p>
                  <div className="sf-mod__badges">
                    <span className="sf-chip sf-chip--locked">Locked</span>
                    <span className="sf-chip">Coming Soon</span>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <div className="sf-foot">
            <Link to="/marketplace">← Oshar Marketplace</Link>
          </div>
        </div>

        <aside className="sf-rail" aria-label="Storefront status and checklist">
          <section className="sf-panel" aria-labelledby="sf-rail-status">
            <h2 id="sf-rail-status" className="sf-panel__title serif">
              {rightRail.storefrontStatus.title}
            </h2>
            <ul className="sf-panel__kv">
              <li>
                <span className="sf-panel__k">Rank</span>
                <span className="sf-panel__v">{rightRail.storefrontStatus.rank}</span>
              </li>
              <li>
                <span className="sf-panel__k">XP</span>
                <span className="sf-panel__v">{rightRail.storefrontStatus.xp}</span>
              </li>
              <li>
                <span className="sf-panel__k">Daily Streak</span>
                <span className="sf-panel__v">{rightRail.storefrontStatus.dailyStreak}</span>
              </li>
              <li>
                <span className="sf-panel__k">Quest XP</span>
                <span className="sf-panel__v">{rightRail.storefrontStatus.questXp}</span>
              </li>
            </ul>
            <button type="button" className="sf-btn-primary">
              {rightRail.storefrontStatus.achievementsCta}
            </button>
          </section>

          <section className="sf-panel" aria-labelledby="sf-rail-checklist">
            <h2 id="sf-rail-checklist" className="sf-panel__title serif">
              {rightRail.launchChecklist.title}
            </h2>
            <ul className="sf-checklist">
              {rightRail.launchChecklist.items.map((item) => (
                <li key={item}>
                  <span>{item}</span>
                  <span className="sf-checklist__soon">Coming Soon</span>
                </li>
              ))}
            </ul>
            <button type="button" className="sf-btn-ghost">
              {rightRail.launchChecklist.updatesCta}
            </button>
          </section>

          <section className="sf-panel sf-panel--utility" aria-labelledby="sf-rail-parcel">
            <h2 id="sf-rail-parcel" className="sf-panel__title serif">
              {rightRail.parcelUtility.title}
            </h2>
            <span className="sf-chip sf-chip--nft" style={{ marginBottom: 10 }}>
              {rightRail.parcelUtility.chip}
            </span>
            <div className="sf-panel__visual">
              <img src={characterArt} alt="" loading="lazy" decoding="async" />
            </div>
            <p className="sf-panel__body">{rightRail.parcelUtility.body}</p>
            <button type="button" className="sf-btn-ghost">
              {rightRail.parcelUtility.learnCta}
            </button>
          </section>
        </aside>
      </div>
    </div>
  );
}
